// A workload's documents as the leased watch delivers them
// (02-DESIGN/06-configuration/01-the-configuration-service.md § the leased
// watch): the snapshot replaces the view, each change puts or deletes one
// entry, and the completion marker says the view is whole. Pure, so that the
// fold is tested without a connection; the page keeps the map in state.

import type { watch } from "@impire-io/sdk";

import { tenantPath } from "../../address/address.ts";
import { documentOf, EntryOp, type Entry } from "../../wire/config.ts";

/** Documents is a workload's documents by name, as the watch last showed them. */
export type Documents = ReadonlyMap<string, Entry>;

/** View is the page's view of one workload's documents: the map, and whether the snapshot has completed. */
export interface View {
  docs: Documents;
  ready: boolean;
}

/** EMPTY is the view before the watch said anything. */
export const EMPTY: View = { docs: new Map(), ready: false };

/** fold applies one watch event to the view and answers the next view. */
export function fold(view: View, ev: watch.Event<Entry>): View {
  switch (ev.kind) {
    case "reset":
      return { docs: new Map(), ready: false };
    case "complete":
      return { docs: view.docs, ready: true };
    case "snapshot":
    case "change": {
      const entry = ev.entry;
      if (entry === undefined) {
        return view;
      }
      const next = new Map(view.docs);
      const name = documentOf(entry.key);
      if (entry.op === EntryOp.delete) {
        next.delete(name);
      } else {
        next.set(name, entry);
      }
      return { docs: next, ready: view.ready };
    }
    default:
      return view;
  }
}

/** sorted lists the documents by name, `settings` first because every platform image reads it. */
export function sorted(docs: Documents, first = "settings"): Entry[] {
  return [...docs.entries()]
    .sort(([a], [b]) => (a === first ? -1 : b === first ? 1 : a < b ? -1 : a > b ? 1 : 0))
    .map(([, e]) => e);
}

/**
 * Row is one line of the Configuration table: one document of one workload
 * (01-the-configuration-service.md § the console: Configuration). A `list`
 * entry carries a key and a revision and no word on the workload, so the
 * page pairs it with the workload the workload service named.
 */
export interface Row {
  /** The entry's key, `workload.<id>.<document>`: unique in the workspace. */
  key: string;
  workload: string;
  /** The workload's display name. */
  name: string;
  /** The workload's image reference, for the field-list hint beside a platform image's settings. */
  image: string;
  document: string;
  revision: number;
}

/** Owner is what a row reads of a workload. */
export interface Owner {
  id: string;
  name: string;
  image: string;
}

/** byWorkload orders rows by the workload's name, then `settings` first and the rest by name: the table's resting order. */
function byWorkload(a: Row, b: Row, first: string): number {
  const n = a.name.localeCompare(b.name) || a.workload.localeCompare(b.workload);
  if (n !== 0) {
    return n;
  }
  if (a.document === b.document) {
    return 0;
  }
  return a.document === first ? -1 : b.document === first ? 1 : a.document.localeCompare(b.document);
}

/**
 * rowsOf pairs each workload's listed documents with the workload: one row
 * per document, a workload with none has no row, and a deletion is no row.
 */
export function rowsOf(
  owners: readonly Owner[],
  lists: ReadonlyMap<string, readonly Entry[]>,
  first = "settings",
): Row[] {
  const rows: Row[] = [];
  for (const w of owners) {
    for (const e of lists.get(w.id) ?? []) {
      if (e.op === EntryOp.delete) {
        continue;
      }
      rows.push(rowOf(w, e));
    }
  }
  return rows.sort((a, b) => byWorkload(a, b, first));
}

function rowOf(w: Owner, e: Entry): Row {
  return {
    key: e.key,
    workload: w.id,
    name: w.name,
    image: w.image,
    document: documentOf(e.key),
    revision: e.revision,
  };
}

/**
 * applied is the table after a save or a delete, from the envelope the reply
 * carried and without reading the table again: a put adds or updates its
 * row, a delete removes it. An entry of a workload the page does not know is
 * left out, as the table leaves out a retired workload's documents.
 */
export function applied(rows: readonly Row[], owners: readonly Owner[], e: Entry, first = "settings"): Row[] {
  const rest = rows.filter((r) => r.key !== e.key);
  if (e.op === EntryOp.delete) {
    return rest;
  }
  const workload = e.key.split(".")[1] ?? "";
  const w = owners.find((o) => o.id === workload);
  if (w === undefined) {
    return rest;
  }
  return [...rest, rowOf(w, e)].sort((a, b) => byWorkload(a, b, first));
}

/**
 * documentAddress is a document's page, `configuration/<workload>/<document>`
 * (01-the-console.md § the address): the page a Configuration row and a
 * workload's Settings row both open.
 */
export function documentAddress(slug: string, workload: string, document: string): string {
  return tenantPath(slug, "configuration", workload, document);
}

/**
 * Back is where a document's page was opened from, other than Configuration:
 * a workload's Settings tab. It travels in the link's state, never in the
 * address, and is the crumb's target and where a delete returns.
 */
export interface Back {
  /** The page, an address of the console's own. */
  to: string;
  /** Its name, as the crumb reads it. */
  label: string;
  /** The tab that page opens on. */
  tab?: string;
}

/** backOf reads where a document's page was opened from out of the navigation's state; nothing that is not the console's own. */
export function backOf(state: unknown): Back | undefined {
  if (typeof state !== "object" || state === null) {
    return undefined;
  }
  const b = (state as { back?: unknown }).back;
  if (typeof b !== "object" || b === null) {
    return undefined;
  }
  const { to, label, tab } = b as Record<string, unknown>;
  if (typeof to !== "string" || !to.startsWith("/") || to.startsWith("//") || typeof label !== "string") {
    return undefined;
  }
  return typeof tab === "string" ? { to, label, tab } : { to, label };
}

/** documentReturn is a document's page's crumb and where its delete goes: where it was opened from, or Configuration. */
export function documentReturn(
  slug: string,
  state: unknown,
): { to: string; label: string; state?: { tab: string } } {
  const b = backOf(state);
  if (b === undefined) {
    return { to: tenantPath(slug, "configuration"), label: "Configuration" };
  }
  return b.tab === undefined
    ? { to: b.to, label: b.label }
    : { to: b.to, label: b.label, state: { tab: b.tab } };
}

/** DocumentView is what a document's page draws: still reading, the editor, a document not saved, or a refusal. */
export type DocumentView = "pending" | "editor" | "missing" | "refused";

/**
 * documentView decides a document's page from its two reads, the workload
 * and the document. A refusal of either is a refusal, never an editor; a
 * document not saved under the name is said, never an empty editor that
 * saves it — a form has no address (decision 0069), and adding is
 * Configuration's *Add settings*.
 */
export function documentView(
  workload: "pending" | "ready" | "refused",
  document: "pending" | "ready" | "missing" | "refused",
): DocumentView {
  if (workload === "refused" || document === "refused") {
    return "refused";
  }
  if (workload === "pending" || document === "pending") {
    return "pending";
  }
  return document === "missing" ? "missing" : "editor";
}
